import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail } from "lucide-react";
import { toast } from "react-toastify";
import contactRequests from "../../api/Customer/ContactRequests";

const ExportButton = ({
  groupSlug,
  groupName,
  contactCount = 0,
  variant = "default",
  className = "",
  disabled = false,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleExport = async () => {
    if (isExporting || !groupSlug || contactCount === 0) return;

    setIsExporting(true);
    setExportProgress(0);

    // Simulate progress for better UX
    const progressInterval = setInterval(() => {
      setExportProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.random() * 15;
      });
    }, 200);

    try {
      const response = await contactRequests.exportContacts(groupSlug);

      clearInterval(progressInterval);
      setExportProgress(100);

      if (response.success) {
        toast.success(
          `✅ Export request submitted! Your contacts will be sent to your email shortly.`
        );
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        toast.error("Failed to submit export request. Please try again.");
      }
    } catch (error) {
      clearInterval(progressInterval);
      toast.error("Error submitting export request. Please try again.");
      console.error("Export error:", error);
    } finally {
      // Reset after a short delay to show completion
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
      }, 1000);
    }
  };

  const isDisabled = disabled || isExporting || contactCount === 0;

  const getButtonStyles = () => {
    const baseStyles =
      "flex items-center gap-2 justify-center font-medium transition-all duration-200 shadow-lg";

    switch (variant) {
      case "compact":
        return `${baseStyles} px-3 py-2 text-sm rounded-lg ${
          isDisabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white hover:shadow-xl"
        }`;
      case "full":
        return `${baseStyles} w-full px-6 py-4 text-base rounded-xl ${
          isDisabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white hover:shadow-xl"
        }`;
      default:
        return `${baseStyles} px-6 py-3 text-base rounded-xl ${
          isDisabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-blue-500 border-blue-400 text-blue-600  hover:from-blue-500 hover:to-blue-700 text-white hover:shadow-xl"
        }`;
    }
  };

  return (
    <>
      <motion.button
        onClick={handleExport}
        disabled={isDisabled}
        className={`${getButtonStyles()} ${className}`}
        whileHover={!isDisabled ? { scale: 1.02 } : {}}
        whileTap={!isDisabled ? { scale: 0.98 } : {}}
        title={
          isExporting
            ? "Submitting..."
            : contactCount === 0
            ? "No contacts to export"
            : `Request export of ${contactCount} contacts via email`
        }
      >
        {isExporting ? (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-5 h-5"
            >
              <div className="w-full h-full rounded-full border-2 border-white border-t-transparent" />
            </motion.div>
            <span>
              {variant === "compact" ? "Submitting..." : "Submitting..."}
            </span>
          </>
        ) : (
          <>
            <Mail className="w-5 h-5" />
            <span>
              {variant === "compact"
                ? "Export"
                : variant === "full"
                ? `Export (${contactCount.toLocaleString()})`
                : "Export"}
            </span>
          </>
        )}
      </motion.button>

      {/* Progress Bar (for full variant) */}
      {isExporting && variant === "full" && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="p-4 mt-4 bg-blue-50 rounded-xl border border-blue-200"
        >
          <div className="flex justify-between items-center mb-3">
            <div className="flex gap-3 items-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-5 h-5"
              >
                <div className="w-full h-full rounded-full border-2 border-blue-600 border-t-transparent" />
              </motion.div>
              <span className="font-medium text-blue-800">
                Submitting Request...
              </span>
            </div>
            <span className="font-bold text-blue-600">
              {Math.round(exportProgress)}%
            </span>
          </div>
          <div className="overflow-hidden w-full h-3 bg-blue-100 rounded-full">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${exportProgress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <p className="mt-2 text-sm text-blue-600">
            {exportProgress < 30 && "Preparing request..."}
            {exportProgress >= 30 &&
              exportProgress < 60 &&
              "Submitting to server..."}
            {exportProgress >= 60 &&
              exportProgress < 90 &&
              "Processing export..."}
            {exportProgress >= 90 && "Finalizing request..."}
          </p>
        </motion.div>
      )}

      {/* Floating Success Notification */}
      {showSuccess && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.8 }}
          className="flex fixed top-4 right-4 z-50 gap-3 items-center px-6 py-4 text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl border border-blue-400 shadow-2xl"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
            className="flex justify-center items-center w-8 h-8 rounded-full bg-white/20"
          >
            <Mail className="w-5 h-5" />
          </motion.div>
          <div>
            <p className="font-semibold">Export Request Submitted!</p>
            <p className="text-sm text-red-100">
              Check your email for the file
            </p>
          </div>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100%" }}
            transition={{ duration: 3, ease: "linear" }}
            className="absolute bottom-0 left-0 h-1 rounded-b-2xl bg-white/30"
          />
        </motion.div>
      )}
    </>
  );
};

export default ExportButton;
